import { CITIZENSHIP_TYPE } from '../../types/eligibility';
import { attributeValuesOf } from './deriveFilterOptions';
import type { FilterableProduct } from './productFilterTypes';

/**
 * The citizenship values the page's own products carry (CMS-1905 UC 1 / AC 2).
 *
 * Never a hardcoded list: a market shows whatever its products distinguish, so
 * adding a product with a new value adds the option with no code change, and a
 * value no listed product carries is not offered — selecting it could only
 * match nothing.
 *
 * Shown exactly as PCT entered them (AC 2e). There is no translation step: the
 * values are authored per market in that market's own language.
 */
export const deriveCitizenshipOptions = (
  products: readonly FilterableProduct[] | undefined,
): string[] => {
  if (!products?.length) return [];

  const distinct = new Set<string>();
  products.forEach((product) => {
    attributeValuesOf(product, CITIZENSHIP_TYPE).forEach((value) =>
      distinct.add(value),
    );
  });

  // Alphabetical by what the reader sees, so the list stays stable as products
  // are added — the same rule `deriveFilterOptions` applies to its own options.
  return [...distinct].sort((a, b) => a.localeCompare(b));
};

/**
 * Whether any product publishes a minimum income, which is what decides
 * whether the income control is offered at all. A market only shows the checks
 * its own lender data supports (UC 8).
 */
export const hasIncomeCriteria = (
  products: readonly FilterableProduct[] | undefined,
  attributeName: string,
): boolean =>
  !!products?.some((product) =>
    product.attributes?.some((a) => a?.name?.name === attributeName),
  );
